import { csv, number } from '../RmMonitor/liveModel';

export const PAGE_ID = 'experience-dash';
export const VIEWS = [
  'Sales Overview',
  'Region Trends',
  'Top Performance',
  'Offer Views',
  'Hotel In-flow or Bundle',
  'Period Comparison',
  'WBR summary',
] as const;

export const TOP_METRICS = ['GM (AR)', 'TTV (AR)', 'GM (BR)', 'TTV (BR)', 'Items (AR)', 'Items (BR)'] as const;
export type TopMetric = (typeof TOP_METRICS)[number];

function salesWindowDays(from: string, to: string): string[] {
  const days: string[] = [];
  let cursor = Date.parse(`${from}T00:00:00Z`);
  const end = Date.parse(`${to}T00:00:00Z`);
  while (cursor <= end) {
    days.push(new Date(cursor).toISOString().slice(0, 10));
    cursor += 86_400_000;
  }
  return days;
}

export type Totals = {
  ttvAr: number;
  gmAr: number;
  ttvBr: number;
  gmBr: number;
  refunds: number;
  itemsBr: number;
  itemsR: number;
  attachNum: number;
  attachDen: number;
};

export type DailyRow = { day: string; brand: string; ttvAr: number; gmAr: number; ttvBr: number; refunds: number };
export type NamedRow = {
  name: string;
  ttvAr: number;
  gmAr: number;
  ttvBr: number;
  gmBr: number;
  itemsBr: number;
  itemsR: number;
  attachNum: number;
  attachDen: number;
};
export type ViewRow = {
  name: string;
  uniqueViews: number;
  totalViews: number;
  items: number;
  addToCart: number;
  ttv: number;
  gm: number;
};
export type OfferRow = ViewRow & { offerBk: string; offerName: string; productName: string };
export type HotelRow = {
  hotelVendor: string;
  hotelOffer: string;
  campaign: string;
  experienceOffer: string;
  experienceOfferBk: string;
  experienceVendor: string;
  ttvAr: number;
  gmAr: number;
  itemsAr: number;
};
export type PeriodRow = {
  period: string;
  ttvBr: number;
  gmBr: number;
  ordersBr: number;
  itemsBr: number;
};
export type WbrRow = { origin: string; timeFrame: string; ttv: number; margin: number; items: number };

const CHANNEL_ORDER = ['Bundle Addon', 'In Flow', 'Post Purchase', 'Standalone'];
const WBR_FRAMES = ['12 Week Avg', 'Last Week', 'Week Prior'];
const ORIGIN_LABEL: Record<string, string> = {
  standalone: 'Standalone',
  bundle_addon: 'Bundle Addon',
  in_flow: 'In Flow',
  post_purchase: 'Post Purchase',
};

function unwrap(value: unknown): unknown {
  if (value && typeof value === 'object' && 'value' in value) return (value as { value: unknown }).value;
  return value;
}

function num(value: unknown): number {
  return number(unwrap(value)) ?? 0;
}

function text(value: unknown): string {
  const raw = unwrap(value);
  return raw == null ? '' : String(raw);
}

function day(value: unknown): string {
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(text(value));
  return match ? match[1] : text(value);
}

export function itemsAr(itemsBr: number, itemsR: number): number {
  return itemsBr - itemsR;
}

export function aiv(ttvAr: number, items: number): number | null {
  return items === 0 ? null : ttvAr / items;
}

export function refundRate(refunds: number, ttvBr: number): number | null {
  return ttvBr === 0 ? null : refunds / ttvBr;
}

export function attachment(numerator: number, denominator: number): number | null {
  return denominator === 0 ? null : numerator / denominator;
}

export function money0(value: number): string {
  const rounded = Math.round(Math.abs(value)).toLocaleString('en-AU');
  return value < 0 ? `-$${rounded}` : `$${rounded}`;
}

export function money2(value: number): string {
  const rounded = Math.abs(value).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return value < 0 ? `-$${rounded}` : `$${rounded}`;
}

export function percent1(value: number | null): string {
  if (value === null) return '';
  return `${(value * 100).toFixed(1)}%`;
}

export function integer(value: number): string {
  return Math.round(value).toLocaleString('en-AU');
}

export function metricValue(row: { ttvAr: number; gmAr: number; ttvBr: number; gmBr: number; itemsBr: number; itemsR: number }, metric: TopMetric): number {
  if (metric === 'TTV (AR)') return row.ttvAr;
  if (metric === 'GM (AR)') return row.gmAr;
  if (metric === 'TTV (BR)') return row.ttvBr;
  if (metric === 'GM (BR)') return row.gmBr;
  if (metric === 'Items (BR)') return row.itemsBr;
  return itemsAr(row.itemsBr, row.itemsR);
}

export function formatMetric(value: number, metric: TopMetric): string {
  return metric.startsWith('Items') ? integer(value) : money0(value);
}

function byName(rows: readonly NamedRow[]): NamedRow[] {
  return [...rows].sort((a, b) => a.name.localeCompare(b.name) || 0);
}

export function byMetric(rows: readonly NamedRow[], metric: TopMetric): NamedRow[] {
  return [...rows].sort((a, b) => metricValue(b, metric) - metricValue(a, metric) || a.name.localeCompare(b.name));
}

export function readTotals(rows: ReadonlyArray<Record<string, unknown>>): Totals | null {
  const row = rows[0];
  if (!row) return null;
  return {
    ttvAr: num(row.ttv_ar),
    gmAr: num(row.gm_ar),
    ttvBr: num(row.ttv_br),
    gmBr: num(row.gm_br),
    refunds: num(row.refunds),
    itemsBr: num(row.items_br),
    itemsR: num(row.items_r),
    attachNum: num(row.attach_num),
    attachDen: num(row.attach_den),
  };
}

export function readDaily(rows: ReadonlyArray<Record<string, unknown>>): DailyRow[] {
  return rows
    .map((row) => ({
      day: day(row.day),
      brand: text(row.brand),
      ttvAr: num(row.ttv_ar),
      gmAr: num(row.gm_ar),
      ttvBr: num(row.ttv_br),
      refunds: num(row.refunds),
    }))
    .filter((row) => row.day)
    .sort((a, b) => a.day.localeCompare(b.day) || a.brand.localeCompare(b.brand));
}

export function dailyForBrand(rows: readonly DailyRow[], brand: string | null, from: string, to: string): DailyRow[] {
  const chosen = brand ? rows.filter((row) => row.brand === brand) : rows;
  const byDay = new Map<string, DailyRow>();
  for (const row of chosen) {
    const current = byDay.get(row.day);
    if (!current) {
      byDay.set(row.day, { ...row, brand: brand ?? '' });
      continue;
    }
    current.ttvAr += row.ttvAr;
    current.gmAr += row.gmAr;
    current.ttvBr += row.ttvBr;
    current.refunds += row.refunds;
  }
  return salesWindowDays(from, to).map(
    (day) => byDay.get(day) ?? { day, brand: brand ?? '', ttvAr: 0, gmAr: 0, ttvBr: 0, refunds: 0 },
  );
}

export function readNamed(rows: ReadonlyArray<Record<string, unknown>>): NamedRow[] {
  return rows.map((row) => ({
    name: text(row.name),
    ttvAr: num(row.ttv_ar),
    gmAr: num(row.gm_ar),
    ttvBr: num(row.ttv_br),
    gmBr: num(row.gm_br),
    itemsBr: num(row.items_br),
    itemsR: num(row.items_r),
    attachNum: num(row.attach_num),
    attachDen: num(row.attach_den),
  }));
}

export function readViews(rows: ReadonlyArray<Record<string, unknown>>): ViewRow[] {
  return rows.map((row) => ({
    name: text(row.name),
    uniqueViews: num(row.unique_views),
    totalViews: num(row.total_views),
    items: num(row.items),
    addToCart: num(row.add_to_cart),
    ttv: num(row.ttv),
    gm: num(row.gm),
  }));
}

export function readOffers(rows: ReadonlyArray<Record<string, unknown>>): OfferRow[] {
  return rows
    .map((row) => ({
      name: text(row.offer_name),
      offerBk: text(row.offer_bk),
      offerName: text(row.offer_name),
      productName: text(row.product_name),
      uniqueViews: num(row.unique_views),
      totalViews: num(row.total_views),
      items: num(row.items),
      addToCart: num(row.add_to_cart),
      ttv: num(row.ttv),
      gm: num(row.gm),
    }))
    .sort((a, b) => offerMpv(a) - offerMpv(b) || b.uniqueViews - a.uniqueViews || a.offerName.localeCompare(b.offerName));
}

export function readHotels(rows: ReadonlyArray<Record<string, unknown>>): HotelRow[] {
  return rows
    .map((row) => ({
      hotelVendor: text(row.hotel_vendor),
      hotelOffer: text(row.hotel_offer),
      campaign: text(row.campaign),
      experienceOffer: text(row.experience_offer),
      experienceOfferBk: text(row.experience_offer_bk),
      experienceVendor: text(row.experience_vendor),
      ttvAr: num(row.ttv_ar),
      gmAr: num(row.gm_ar),
      itemsAr: itemsAr(num(row.items_br), num(row.items_r)),
    }))
    .sort((a, b) => b.ttvAr - a.ttvAr || a.experienceOffer.localeCompare(b.experienceOffer));
}

export function readPeriods(rows: ReadonlyArray<Record<string, unknown>>): PeriodRow[] {
  return rows
    .map((row) => ({
      period: text(row.period),
      ttvBr: num(row.ttv_br),
      gmBr: num(row.gm_br),
      ordersBr: num(row.orders_br),
      itemsBr: num(row.items_br),
    }))
    .sort((a, b) => a.period.localeCompare(b.period));
}

export function offerMpv(row: { gm: number; uniqueViews: number }): number {
  return row.uniqueViews === 0 ? 0 : row.gm / row.uniqueViews;
}

export function headlineForBrand(totals: Totals, brand: NamedRow | null): Totals {
  if (!brand) return totals;
  return {
    ttvAr: brand.ttvAr,
    gmAr: brand.gmAr,
    ttvBr: brand.ttvBr,
    gmBr: brand.gmBr,
    refunds: brand.ttvBr - brand.ttvAr,
    itemsBr: brand.itemsBr,
    itemsR: brand.itemsR,
    attachNum: brand.attachNum,
    attachDen: brand.attachDen,
  };
}

export type ViewsOverTimeRow = { day: string; channel: string; uniqueViews: number; gm: number; mpv: number };

export function readViewsOverTime(rows: ReadonlyArray<Record<string, unknown>>): ViewsOverTimeRow[] {
  return rows
    .map((row) => {
      const uniqueViews = num(row.unique_views);
      const gm = num(row.gm);
      return { day: day(row.day), channel: text(row.channel), uniqueViews, gm, mpv: uniqueViews === 0 ? 0 : gm / uniqueViews };
    })
    .filter((row) => row.day)
    .sort((a, b) => a.day.localeCompare(b.day) || b.mpv - a.mpv || a.channel.localeCompare(b.channel));
}

export type VendorSlice = NamedRow & { vendor: string };

export function readVendorSlices(rows: ReadonlyArray<Record<string, unknown>>): VendorSlice[] {
  return rows.map((row) => ({
    name: text(row.name),
    vendor: text(row.vendor),
    ttvAr: num(row.ttv_ar),
    gmAr: num(row.gm_ar),
    ttvBr: num(row.ttv_br),
    gmBr: num(row.gm_br),
    itemsBr: num(row.items_br),
    itemsR: num(row.items_r),
    attachNum: 0,
    attachDen: 0,
  }));
}

export function totalsForVendor(rows: readonly VendorSlice[], vendor: string | null): NamedRow[] {
  const chosen = vendor ? rows.filter((row) => row.vendor === vendor) : rows;
  const byName = new Map<string, NamedRow>();
  for (const row of chosen) {
    const current = byName.get(row.name);
    if (!current) {
      byName.set(row.name, { ...row });
      continue;
    }
    current.ttvAr += row.ttvAr;
    current.gmAr += row.gmAr;
    current.ttvBr += row.ttvBr;
    current.gmBr += row.gmBr;
    current.itemsBr += row.itemsBr;
    current.itemsR += row.itemsR;
  }
  return [...byName.values()];
}

export function readWbr(rows: ReadonlyArray<Record<string, unknown>>): WbrRow[] {
  return rows
    .map((row) => {
      const raw = text(row.experience_purchase_origin);
      return {
        origin: ORIGIN_LABEL[raw] ?? raw,
        timeFrame: text(row.time_frame),
        ttv: num(row.ttv),
        margin: num(row.margin),
        items: num(row.items),
      };
    })
    .sort((a, b) => a.origin.localeCompare(b.origin) || a.timeFrame.localeCompare(b.timeFrame));
}

export function channelsAlpha(rows: readonly NamedRow[]): NamedRow[] {
  return byName(rows);
}

export function filterName(rows: readonly NamedRow[], selected: string | null): NamedRow[] {
  if (!selected) return [...rows];
  return rows.filter((row) => row.name === selected);
}

export function toggleSelection(current: string | null, next: string): string | null {
  return current === next ? null : next;
}

export function sumDailyTtv(rows: readonly DailyRow[]): number {
  return rows.reduce((total, row) => total + row.ttvAr, 0);
}

export function viewRates(row: { uniqueViews: number; totalViews: number; items: number; addToCart: number; ttv: number; gm: number }) {
  const unique = row.uniqueViews;
  return {
    mpv: unique === 0 ? null : row.gm / unique,
    rpv: unique === 0 ? null : row.ttv / unique,
    cr: unique === 0 ? null : (row.items / unique) * 100,
    atc: unique === 0 ? null : (row.addToCart / unique) * 100,
  };
}

export function sumViews(rows: readonly ViewRow[]): ViewRow {
  return rows.reduce(
    (total, row) => ({
      name: 'All',
      uniqueViews: total.uniqueViews + row.uniqueViews,
      totalViews: total.totalViews + row.totalViews,
      items: total.items + row.items,
      addToCart: total.addToCart + row.addToCart,
      ttv: total.ttv + row.ttv,
      gm: total.gm + row.gm,
    }),
    { name: 'All', uniqueViews: 0, totalViews: 0, items: 0, addToCart: 0, ttv: 0, gm: 0 },
  );
}

export function dailyCsv(rows: readonly DailyRow[]): string {
  return csv(
    rows.map((row) => ({ Day: row.day, 'TTV (AR)': row.ttvAr, 'GM (AR)': row.gmAr, 'TTV (BR)': row.ttvBr, 'Refunds (TTV)': row.refunds })),
    ['Day', 'TTV (AR)', 'GM (AR)', 'TTV (BR)', 'Refunds (TTV)'],
  );
}

export function namedCsv(rows: readonly NamedRow[], metric: TopMetric): string {
  return csv(
    rows.map((row) => ({
      Name: row.name,
      'TTV (AR)': row.ttvAr,
      'GM (AR)': row.gmAr,
      'TTV (BR)': row.ttvBr,
      'GM (BR)': row.gmBr,
      'Items (AR)': itemsAr(row.itemsBr, row.itemsR),
      [metric]: metricValue(row, metric),
    })),
    ['Name', 'TTV (AR)', 'GM (AR)', 'TTV (BR)', 'GM (BR)', 'Items (AR)'],
  );
}

export function offerCsv(rows: readonly OfferRow[]): string {
  return csv(
    rows.map((row) => ({
      'Offer ID': row.offerBk,
      Offer: row.offerName,
      Product: row.productName,
      'Unique Views': row.uniqueViews,
      'Total Views': row.totalViews,
      Items: row.items,
      TTV: row.ttv,
      GM: row.gm,
    })),
    ['Offer ID', 'Offer', 'Product', 'Unique Views', 'Total Views', 'Items', 'TTV', 'GM'],
  );
}

export function hotelCsv(rows: readonly HotelRow[]): string {
  return csv(
    rows.map((row) => ({
      'Hotel vendor': row.hotelVendor,
      'Hotel offer': row.hotelOffer,
      Campaign: row.campaign,
      'Experience offer': row.experienceOffer,
      'Experience vendor': row.experienceVendor,
      'TTV (AR)': row.ttvAr,
      'GM (AR)': row.gmAr,
      'Items (AR)': row.itemsAr,
    })),
    ['Hotel vendor', 'Hotel offer', 'Campaign', 'Experience offer', 'Experience vendor', 'TTV (AR)', 'GM (AR)', 'Items (AR)'],
  );
}

export function periodCsv(rows: readonly PeriodRow[]): string {
  return csv(
    rows.map((row) => ({ Period: row.period, 'TTV (BR)': row.ttvBr, 'GM (BR)': row.gmBr, 'Orders (BR)': row.ordersBr, 'Items (BR)': row.itemsBr })),
    ['Period', 'TTV (BR)', 'GM (BR)', 'Orders (BR)', 'Items (BR)'],
  );
}

export function wbrCsv(rows: readonly WbrRow[]): string {
  return csv(
    rows.map((row) => ({ Experience: row.origin, 'Time frame': row.timeFrame, TTV: row.ttv, Margin: row.margin, Items: row.items })),
    ['Experience', 'Time frame', 'TTV', 'Margin', 'Items'],
  );
}

export function wbrFrames(): readonly string[] {
  return WBR_FRAMES;
}

export function channelOrder(name: string): number {
  const index = CHANNEL_ORDER.indexOf(name);
  return index === -1 ? CHANNEL_ORDER.length : index;
}
